/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from 'react'

import { useSafeThemeUI } from '../theme-provider/use-theme-ui'
import type { Breakpoints } from '../themes/theme-keys/breakpoints'
import { defaultBreakpoints } from '../themes/theme-keys/breakpoints'
import { isBrowser } from '../utils/dom'
import executeWhenChangeStop from '../utils/execute-when-change-stop'
import { memoizedGet as get } from '../utils/object'

type defaultOptions = {
  defaultIndex?: number
  updateOnResizeChangeStopPeriod?: number
}

export const useBreakpointIndex = (options: defaultOptions = {}): number => {
  const theme = useSafeThemeUI()
  const { defaultIndex = 0, updateOnResizeChangeStopPeriod = 0 } = options
  const breakpoints: Breakpoints = get(theme, 'breakpoints', defaultBreakpoints)

  const getIndex = useCallback(() => {
    if (!isBrowser) {
      if (typeof defaultIndex === 'number') {
        if (defaultIndex < 0 || defaultIndex > breakpoints.keys.length - 1) {
          throw new RangeError(
            `Default breakpoint index out of range. Theme has ${breakpoints.keys.length} breakpoints, got index ${defaultIndex}`
          )
        }
        return defaultIndex
      }
      throw new TypeError(
        `Default breakpoint index should be a number. Got: ${defaultIndex}, ${typeof defaultIndex}`
      )
    }

    return breakpoints.keys.filter(key => {
      if (process.env.NODE_ENV !== 'test') {
        return (
          breakpoints.getValueAndUnit(key) &&
          window.matchMedia(
            `screen and (min-width: ${breakpoints.getValueAndUnit(key)})`
          ).matches
        )
      }
      return breakpoints.getValueAndUnit(key)
    }).length
  }, [breakpoints, defaultIndex])

  const [value, setValue] = useState(getIndex)

  useEffect(() => {
    const [onResize, reset] = executeWhenChangeStop(() => {
      const newValue = getIndex()
      if (value !== newValue) {
        setValue(newValue)
      }
    }, updateOnResizeChangeStopPeriod)
    if (process.env.NODE_ENV !== 'test') {
      isBrowser && window.addEventListener('resize', onResize)
    }
    return () => {
      reset()
      if (process.env.NODE_ENV !== 'test') {
        isBrowser && window.removeEventListener('resize', onResize)
      }
    }
  }, [breakpoints, getIndex, value, updateOnResizeChangeStopPeriod])

  return value
}

type Values<T> = ((theme?: any) => T[]) | T[]

export default function useResponsiveValue<T>(
  values: Values<T>,
  options: defaultOptions = {}
): T {
  const theme = useSafeThemeUI()
  const array = typeof values === 'function' ? values(theme) : values
  const safeArray = array.reduce((acc: T[], cur: T, indx) => {
    const next =
      (cur === null || cur === undefined) && indx > 0 ? array[indx - 1] : cur
    return [...acc, next]
  }, [])
  const index = useBreakpointIndex(options)
  return safeArray[index >= safeArray.length ? safeArray.length - 1 : index]
}
